import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { ChevronLeft, ChevronRight, CheckCircle, BookOpen, Plus, Calendar, Headphones, Feather, Lock, Globe, Mic, MicOff, X, Check, Loader } from 'lucide-react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import AddToLibraryModal from '../../bible-reader/components/AddToLibraryModal';
import ReadingCalendar from './ReadingCalendar';
import { readingPlanService, reflectionService } from '../../../services/supabaseService';
import { useAudioPlayer } from '../../../hooks/useAudioPlayer';
import { useAuth } from '../../../contexts/AuthContext';
import { supabase } from '../../../lib/supabase';

// Parse a reference string like "Genesis 1" or "Genesis 1:1-10" → { book: "Genesis", chapter: 1 }
function parseReference(referenceText) {
  if (!referenceText) return null;
  const match = referenceText?.match(/^(.+?)\s+(\d+)(?::\d+(?:-\d+)?)?$/);
  if (match) {
    return { book: match?.[1]?.trim(), chapter: parseInt(match?.[2], 10) };
  }
  return null;
}

const stripStrongs = (text) => {
  if (!text) return '';
  return text?.replace(/\{(?:\()?[HG]\d+(?:\))?\}/g, '')?.trim();
};

const PRESET_TAGS = [
  'Christ and the Church',
  'Habits to Release',
  'Great Controversy',
  'Nature Lessons',
  'History Lessons',
  'Personal Testimony',
];

// Inline reflection form — same design as VerseReflectionAccordion in bible-reader
const InlineReflectionForm = ({ verse, version, onClose, onSaved }) => {
  const { user } = useAuth();
  const [isPrivate, setIsPrivate] = useState(true);
  const [selectedTags, setSelectedTags] = useState([]);
  const [customTagInput, setCustomTagInput] = useState('');
  const [showCustomInput, setShowCustomInput] = useState(false);
  const [meditationText, setMeditationText] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  const recognitionRef = useRef(null);
  const customInputRef = useRef(null);

  const hasSpeechSupport =
    typeof window !== 'undefined' && ('SpeechRecognition' in window || 'webkitSpeechRecognition' in window);

  useEffect(() => {
    if (!hasSpeechSupport) return;
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    const rec = new SR();
    rec.continuous = true;
    rec.interimResults = true;
    rec.lang = 'en-US';
    rec.onresult = (event) => {
      let final = '';
      for (let i = event?.resultIndex; i < event?.results?.length; i++) {
        if (event?.results?.[i]?.isFinal) final += event?.results?.[i]?.[0]?.transcript;
      }
      if (final) setMeditationText((prev) => (prev ? prev + ' ' + final?.trim() : final?.trim()));
    };
    rec.onerror = () => setIsRecording(false);
    rec.onend = () => setIsRecording(false);
    recognitionRef.current = rec;
    return () => { try { rec?.stop(); } catch (_) {} };
  }, [hasSpeechSupport]);

  const toggleTag = (tag) => {
    setSelectedTags((prev) => prev?.includes(tag) ? prev?.filter((t) => t !== tag) : [...prev, tag]);
  };

  const addCustomTag = () => {
    const trimmed = customTagInput?.trim();
    if (!trimmed) return;
    if (!selectedTags?.includes(trimmed)) setSelectedTags((prev) => [...prev, trimmed]);
    setCustomTagInput('');
    setShowCustomInput(false);
  };

  const handleCustomKeyDown = (e) => {
    if (e?.key === 'Enter') { e?.preventDefault(); addCustomTag(); }
    if (e?.key === 'Escape') { setShowCustomInput(false); setCustomTagInput(''); }
  };

  const toggleDictation = () => {
    if (!recognitionRef?.current) return;
    if (isRecording) { recognitionRef?.current?.stop(); setIsRecording(false); }
    else { try { recognitionRef?.current?.start(); setIsRecording(true); } catch (_) { setIsRecording(false); } }
  };

  const handleClearAll = () => {
    setSelectedTags([]); setCustomTagInput(''); setShowCustomInput(false);
    setMeditationText(''); setSaveError(null); setSaveSuccess(false);
    if (isRecording) { try { recognitionRef?.current?.stop(); } catch (_) {} setIsRecording(false); }
  };

  const handleCancel = () => {
    if (isRecording) { try { recognitionRef?.current?.stop(); } catch (_) {} setIsRecording(false); }
    onClose?.();
  };

  const handleSave = async () => {
    if (!user?.id) { setSaveError('Please sign in to save reflections.'); return; }
    if (!meditationText?.trim() && selectedTags?.length === 0) {
      setSaveError('Please add a tag or write your meditation before saving.');
      return;
    }
    setIsSaving(true); setSaveError(null);
    try {
      const verseRef = verse?.reference || `${verse?.book_name} ${verse?.chapter}:${verse?.verse_number}`;
      let verseId = null;
      const { data: existingVerse } = await supabase?.from('verses')?.select('id')?.eq('user_id', user?.id)?.eq('reference', verseRef)?.maybeSingle();
      if (existingVerse) {
        verseId = existingVerse?.id;
      } else {
        const refMatch = verseRef?.match(/^([\w\s]+)\s+(\d+):(\d+)(?:-(\d+))?$/);
        if (refMatch) {
          const [, bookName, chapterNum, startVerse, endVerse] = refMatch;
          const { data: newVerse, error: verseErr } = await supabase?.from('verses')?.insert({
            user_id: user?.id, reference: verseRef, text: stripStrongs(verse?.text),
            book: bookName, chapter: parseInt(chapterNum), verse_start: parseInt(startVerse),
            verse_end: endVerse ? parseInt(endVerse) : null, bible_version: version || 'kjv',
            current_phase: 1, current_interval: 1.0, next_review_date: new Date()?.toISOString()?.split('T')?.[0],
          })?.select('id')?.single();
          if (!verseErr && newVerse) verseId = newVerse?.id;
        }
      }
      const reflectionData = {
        user_id: user?.id, verse_id: verseId, is_private: isPrivate, tags: selectedTags,
        meditation_text: meditationText?.trim() || null,
        book: verse?.book_name, chapter: parseInt(verse?.chapter),
        verse_number: verse?.verse_number, verse_reference: verseRef,
        verse_text: stripStrongs(verse?.text),
      };
      const { error } = await reflectionService?.createReflection(reflectionData);
      if (error) throw error;
      setSaveSuccess(true);
      setTimeout(() => { onSaved?.(); onClose?.(); }, 800);
    } catch (err) {
      setSaveError(err?.message || 'Failed to save. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const verseText = stripStrongs(verse?.text);

  return (
    <div className="mt-2 rounded-xl border border-border bg-background/95 shadow-sm overflow-hidden">
      {/* Header bar */}
      <div className="flex items-center justify-between px-3 py-2 bg-muted/30 border-b border-border">
        <div className="flex items-center gap-1.5">
          <Feather size={13} className="text-primary" />
          <span className="text-xs font-semibold text-foreground tracking-wide uppercase">Meditate</span>
        </div>
        <button onClick={handleCancel} className="p-1 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors" aria-label="Close reflection">
          <X size={14} />
        </button>
      </div>
      <div className="p-3 space-y-3">
        {/* Verse snippet */}
        <p className="text-xs text-muted-foreground italic leading-relaxed line-clamp-2">&ldquo;{verseText}&rdquo;</p>

        {/* Privacy toggle */}
        <div className="flex items-center gap-2">
          <button onClick={() => setIsPrivate(true)} className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border transition-all ${isPrivate ? 'bg-primary text-primary-foreground border-primary' : 'bg-transparent text-muted-foreground border-border hover:border-primary/40'}`}>
            <Lock size={11} /> Private
          </button>
          <button onClick={() => setIsPrivate(false)} className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border transition-all ${!isPrivate ? 'bg-primary text-primary-foreground border-primary' : 'bg-transparent text-muted-foreground border-border hover:border-primary/40'}`}>
            <Globe size={11} /> Public
          </button>
        </div>

        {/* Tags */}
        <div>
          <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">Tags</p>
          <div className="flex flex-wrap gap-1.5">
            {PRESET_TAGS?.map((tag) => {
              const active = selectedTags?.includes(tag);
              return (
                <button key={tag} onClick={() => toggleTag(tag)} className={`px-2 py-0.5 rounded-full text-xs border transition-all ${active ? 'bg-primary/10 text-primary border-primary/40 font-medium' : 'bg-transparent text-muted-foreground border-border hover:border-primary/30 hover:text-foreground'}`}>
                  {active && <span className="mr-1">✓</span>}{tag}
                </button>
              );
            })}
            {selectedTags?.filter((t) => !PRESET_TAGS?.includes(t))?.map((tag) => (
              <button key={tag} onClick={() => toggleTag(tag)} className="px-2 py-0.5 rounded-full text-xs border bg-primary/10 text-primary border-primary/40 font-medium transition-all">
                ✓ {tag}<span className="ml-1 opacity-60">×</span>
              </button>
            ))}
            {showCustomInput ? (
              <div className="flex items-center gap-1">
                <input ref={customInputRef} autoFocus value={customTagInput} onChange={(e) => setCustomTagInput(e?.target?.value)} onKeyDown={handleCustomKeyDown} placeholder="Tag name…" className="px-2 py-0.5 rounded-full text-xs border border-primary/40 bg-background text-foreground outline-none w-28 focus:ring-1 focus:ring-primary/30" />
                <button onClick={addCustomTag} className="p-0.5 rounded-full bg-primary text-primary-foreground"><Check size={10} /></button>
                <button onClick={() => { setShowCustomInput(false); setCustomTagInput(''); }} className="p-0.5 rounded-full text-muted-foreground hover:text-foreground"><X size={10} /></button>
              </div>
            ) : (
              <button onClick={() => setShowCustomInput(true)} className="px-2 py-0.5 rounded-full text-xs border border-dashed border-border text-muted-foreground hover:border-primary/40 hover:text-primary transition-all">+ Custom</button>
            )}
          </div>
        </div>

        {/* Meditation textarea */}
        <div className="relative">
          <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">Meditation</p>
          <div className="relative">
            <textarea value={meditationText} onChange={(e) => setMeditationText(e?.target?.value)} placeholder="Write your meditation on this verse…" rows={3} className={`w-full text-sm text-foreground bg-muted/20 border rounded-lg px-3 py-2 pr-10 resize-none outline-none focus:ring-1 focus:ring-primary/30 focus:border-primary/40 placeholder:text-muted-foreground/50 transition-all ${isRecording ? 'border-red-400 ring-1 ring-red-300' : 'border-border'}`} />
            {hasSpeechSupport && (
              <button onClick={toggleDictation} title={isRecording ? 'Stop dictation' : 'Start dictation'} className={`absolute right-2 bottom-2 p-1.5 rounded-full transition-all ${isRecording ? 'bg-red-500 text-white animate-pulse' : 'bg-muted text-muted-foreground hover:bg-primary/10 hover:text-primary'}`}>
                {isRecording ? <MicOff size={13} /> : <Mic size={13} />}
              </button>
            )}
          </div>
          {isRecording && <p className="text-[10px] text-red-500 mt-1 flex items-center gap-1"><span className="inline-block w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />Listening… speak your meditation</p>}
        </div>

        {/* Error / Success */}
        {saveError && <p className="text-xs text-destructive bg-destructive/10 px-2 py-1.5 rounded-lg">{saveError}</p>}
        {saveSuccess && <p className="text-xs text-green-600 bg-green-50 px-2 py-1.5 rounded-lg flex items-center gap-1"><Check size={12} />Meditation saved!</p>}

        {/* Actions */}
        <div className="flex items-center justify-between pt-1">
          <button onClick={handleClearAll} className="text-xs text-muted-foreground hover:text-destructive transition-colors">Clear All</button>
          <div className="flex items-center gap-2">
            <button onClick={handleCancel} className="px-3 py-1 text-xs rounded-lg border border-border text-muted-foreground hover:text-foreground hover:border-foreground/30 transition-all">Cancel</button>
            <button onClick={handleSave} disabled={isSaving || saveSuccess} className="px-3 py-1 text-xs rounded-lg bg-primary text-primary-foreground font-medium hover:bg-primary/90 disabled:opacity-50 transition-all flex items-center gap-1">
              {isSaving ? <><Loader size={11} className="animate-spin" /> Saving…</> : saveSuccess ? <><Check size={11} /> Saved!</> : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const ReadingInterface = ({
  reading,
  plan,
  onComplete,
  onAddVerse,
  onBack,
  onDayChange,
  className = ''
}) => {
  const [currentPassageIndex, setCurrentPassageIndex] = useState(0);
  const [completedPassages, setCompletedPassages] = useState(new Set());
  const [reflectingVerseId, setReflectingVerseId] = useState(null);
  const [dayReading, setDayReading] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentDay, setCurrentDay] = useState(plan?.current_day || 1);
  const [savingProgress, setSavingProgress] = useState(false);
  const [showCalendar, setShowCalendar] = useState(false);
  const [isAddToLibraryOpen, setIsAddToLibraryOpen] = useState(false);
  const [addToLibraryVerse, setAddToLibraryVerse] = useState(null);

  // Audio player context — same drawer used in bible-reader
  const { openPlayer } = useAudioPlayer();

  // Swipe gesture refs
  const touchStartX = useRef(null);
  const touchStartY = useRef(null);

  // Fetch actual Bible verses for the current day
  useEffect(() => {
    if (plan?.id && currentDay) {
      fetchDayReading();
    }
  }, [plan?.id, currentDay]);

  const fetchDayReading = async () => {
    setLoading(true);
    setError(null);
    try {
      const bibleVersion = plan?.preferred_bible_version || 'kjv';
      const { data, error: fetchError } = await readingPlanService?.getDayReadingVerses(
        plan?.id,
        currentDay,
        bibleVersion
      );

      if (fetchError) throw fetchError;

      setDayReading(data);
      
      if (data?.references) {
        await loadDayProgress(data?.references);
      }
    } catch (err) {
      console.error('Error fetching day reading:', err);
      setError('Failed to load reading. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const loadDayProgress = async (references) => {
    try {
      const progress = await readingPlanService?.getDayProgress(plan?.id, currentDay);
      
      const completedIndices = new Set();
      references?.forEach((ref, index) => {
        if (progress?.completed?.includes(ref?.id)) {
          completedIndices?.add(index);
        }
      });
      
      setCompletedPassages(completedIndices);
    } catch (err) {
      console.error('Error loading day progress:', err);
    }
  };

  const currentPassage = dayReading?.references?.[currentPassageIndex];
  const totalPassages = dayReading?.references?.length || 0;
  const allCompleted = completedPassages?.size === totalPassages && totalPassages > 0;
  const maxDay = plan?.reading_plans?.duration_days || 365;

  useEffect(() => {
    if (completedPassages?.has(currentPassageIndex) && currentPassageIndex < totalPassages - 1) {
      const timer = setTimeout(() => {
        setCurrentPassageIndex(prev => prev + 1);
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [completedPassages, currentPassageIndex, totalPassages]);

  const handleMarkComplete = async () => {
    if (!currentPassage?.id || !plan?.id) return;

    setSavingProgress(true);
    try {
      const { error: markError } = await readingPlanService?.markReferenceComplete(
        plan?.id,
        currentPassage?.id
      );

      if (markError) throw markError;

      const newCompleted = new Set(completedPassages);
      newCompleted?.add(currentPassageIndex);
      setCompletedPassages(newCompleted);
      
      if (newCompleted?.size === totalPassages) {
        setTimeout(() => {
          onComplete?.(dayReading?.references);
        }, 1000);
      }
    } catch (err) {
      console.error('Error marking passage complete:', err);
      alert('Failed to save progress. Please try again.');
    } finally {
      setSavingProgress(false);
    }
  };

  const handleNavigateDay = async (newDay) => {
    if (newDay < 1 || newDay > maxDay) return;
    
    const { error: updateError } = await readingPlanService?.updateCurrentDay(plan?.id, newDay);
    if (updateError) {
      console.error('Error updating current day:', updateError);
    }
    
    setCurrentDay(newDay);
    setCurrentPassageIndex(0);
    setCompletedPassages(new Set());
    setShowCalendar(false);
    setReflectingVerseId(null);
    
    onDayChange?.(newDay);
  };

  const handlePreviousDay = () => {
    if (currentDay > 1) handleNavigateDay(currentDay - 1);
  };

  const handleNextDay = () => {
    if (currentDay < maxDay) handleNavigateDay(currentDay + 1);
  };

  const handleOpenAudio = () => {
    const passage = currentPassage;
    if (!passage) return;
    const parsed = parseReference(passage?.reference_text);
    if (parsed?.book && parsed?.chapter) { openPlayer(parsed?.book, parsed?.chapter); return; }
    const firstVerse = passage?.verses?.[0];
    if (firstVerse?.book_name && firstVerse?.chapter) openPlayer(firstVerse?.book_name, firstVerse?.chapter);
  };

  const handleTouchStart = useCallback((e) => {
    touchStartX.current = e?.touches?.[0]?.clientX ?? null;
    touchStartY.current = e?.touches?.[0]?.clientY ?? null;
  }, []);

  const handleTouchEnd = useCallback((e) => {
    if (touchStartX?.current === null) return;
    const deltaX = (e?.changedTouches?.[0]?.clientX ?? 0) - touchStartX?.current;
    const deltaY = (e?.changedTouches?.[0]?.clientY ?? 0) - touchStartY?.current;
    if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) >= 50) {
      if (deltaX < 0) {
        if (currentPassageIndex < totalPassages - 1) setCurrentPassageIndex(prev => prev + 1);
        else if (currentDay < maxDay) handleNavigateDay(currentDay + 1);
      } else {
        if (currentPassageIndex > 0) setCurrentPassageIndex(prev => prev - 1);
        else if (currentDay > 1) handleNavigateDay(currentDay - 1);
      }
    }
    touchStartX.current = null;
    touchStartY.current = null;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPassageIndex, totalPassages, currentDay, maxDay]);

  const calculateReadingTime = (verses) => {
    if (!verses || verses?.length === 0) return '0 min';
    const totalWords = verses?.length * 26;
    const minutes = Math.ceil(totalWords / 238);
    return `${minutes} min`;
  };

  const handleVerseReflect = (verse) => {
    const verseKey = `${verse?.book_name}-${verse?.chapter}-${verse?.verse_number}`;
    setReflectingVerseId(prev => prev === verseKey ? null : verseKey);
  };

  const handleAddVerseToLibrary = (verse) => {
    const versesForModal = currentPassage?.verses?.map(v => ({
      number: v?.verse_number,
      text: v?.text,
    })) || [];

    setAddToLibraryVerse({
      book: verse?.book_name || currentPassage?.verses?.[0]?.book_name || '',
      chapter: verse?.chapter || currentPassage?.verses?.[0]?.chapter || 1,
      version: plan?.preferred_bible_version?.toUpperCase() || 'KJV',
      verses: versesForModal,
      selectedVerseNumber: verse?.verse_number,
    });
    setIsAddToLibraryOpen(true);
  };

  const handlePreviousPassage = () => {
    if (currentPassageIndex > 0) setCurrentPassageIndex(currentPassageIndex - 1);
  };

  const handleNextPassage = () => {
    if (currentPassageIndex < totalPassages - 1) setCurrentPassageIndex(currentPassageIndex + 1);
  };

  if (loading) {
    return (
      <div className={`space-y-6 ${className}`}>
        <div className="bg-card rounded-lg border border-border p-6">
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <Icon name="Loader" size={32} color="var(--color-primary)" className="animate-spin mx-auto mb-2" />
              <p className="text-muted-foreground">Loading reading...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !dayReading) {
    return (
      <div className={`space-y-6 ${className}`}>
        <div className="bg-card rounded-lg border border-border p-6">
          <Button variant="ghost" size="sm" onClick={onBack} className="text-muted-foreground hover:text-foreground mb-4">
            <ChevronLeft className="w-4 h-4 mr-1" />Back to Plans
          </Button>
          <div className="text-center py-12">
            <Icon name="AlertCircle" size={48} color="var(--color-destructive)" className="mx-auto mb-4" />
            <p className="text-foreground font-medium mb-2">Unable to Load Reading</p>
            <p className="text-muted-foreground mb-4">{error || 'No reading data available'}</p>
            <Button onClick={fetchDayReading}>Try Again</Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className={`space-y-6 ${className}`} onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>
        {/* Reading Header */}
        <div className="bg-card rounded-lg border border-border p-6">
          <div className="flex items-center justify-between mb-4">
            <Button variant="ghost" size="sm" onClick={onBack} className="text-muted-foreground hover:text-foreground">
              <ChevronLeft className="w-4 h-4 mr-1" />Back to Plans
            </Button>
            
            <div className="flex items-center space-x-2">
              <button onClick={handleOpenAudio} title="Listen to this chapter" className="flex items-center justify-center w-9 h-9 rounded-full bg-amber-500/15 hover:bg-amber-500/25 border border-amber-500/30 hover:border-amber-500/60 text-amber-500 transition-all duration-200 active:scale-95" aria-label="Open audio player">
                <Headphones className="w-4 h-4" />
              </button>
              <Button variant="outline" size="sm" onClick={() => setShowCalendar(!showCalendar)} className="flex items-center space-x-2">
                <Calendar className="w-4 h-4" />
                <span className="hidden sm:inline">{showCalendar ? 'Hide Calendar' : 'Show Calendar'}</span>
              </Button>
              <div className="text-right">
                <p className="text-sm text-muted-foreground">Day {currentDay} of {maxDay}</p>
                <p className="text-xs text-muted-foreground hidden sm:block">{plan?.reading_plans?.name}</p>
              </div>
            </div>
          </div>

          {/* Day Navigation */}
          <div className="flex items-center justify-between mb-4 p-3 bg-muted/30 rounded-lg">
            <Button variant="outline" size="sm" onClick={handlePreviousDay} disabled={currentDay <= 1} className="min-h-[44px] px-3 sm:px-4">
              <ChevronLeft className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">Previous Day</span>
            </Button>
            <div className="flex items-center space-x-2">
              <Calendar className="w-4 h-4 text-muted-foreground" />
              <span className="text-sm font-medium text-foreground">Day {currentDay}</span>
            </div>
            <Button variant="outline" size="sm" onClick={handleNextDay} disabled={currentDay >= maxDay} className="min-h-[44px] px-3 sm:px-4">
              <span className="hidden sm:inline">Next Day</span><ChevronRight className="w-4 h-4 sm:ml-1" />
            </Button>
          </div>

          <div className="mb-4">
            <h1 className="text-2xl font-heading font-bold text-foreground mb-2">{dayReading?.title || `Day ${currentDay} Reading`}</h1>
            <p className="text-muted-foreground">{reading?.date}</p>
            {dayReading?.description && <p className="text-sm text-muted-foreground mt-2">{dayReading?.description}</p>}
          </div>

          {/* Progress Bar */}
          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-foreground">Progress: {completedPassages?.size} of {totalPassages} passages</span>
              <span className="text-sm text-muted-foreground uppercase">{plan?.preferred_bible_version || 'KJV'}</span>
            </div>
            <div className="w-full bg-muted rounded-full h-2">
              <div className="bg-primary h-2 rounded-full transition-all duration-300" style={{ width: `${totalPassages > 0 ? (completedPassages?.size / totalPassages) * 100 : 0}%` }} />
            </div>
          </div>

          {/* Passage Navigation */}
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center space-x-2 flex-wrap gap-1">
              {dayReading?.references?.map((passage, index) => {
                const isActive = index === currentPassageIndex;
                const isCompleted = completedPassages?.has(index);
                return (
                  <button key={index} onClick={() => setCurrentPassageIndex(index)} className={`min-w-[44px] min-h-[44px] px-3 py-2 rounded-lg text-sm font-medium border transition-all duration-200 ${isCompleted ? 'bg-success text-success-foreground border-success' : isActive ? 'bg-primary text-primary-foreground border-primary' : 'bg-muted text-muted-foreground border-border hover:border-primary/30'}`}>
                    {isCompleted ? <CheckCircle className="w-4 h-4" /> : <span>{index + 1}</span>}
                  </button>
                );
              })}
            </div>
            <div className="flex items-center space-x-2">
              <Button variant="outline" size="sm" onClick={handlePreviousPassage} disabled={currentPassageIndex === 0} className="min-h-[44px]">
                <ChevronLeft className="w-4 h-4" /><span className="hidden sm:inline ml-1">Previous</span>
              </Button>
              <Button variant="outline" size="sm" onClick={handleNextPassage} disabled={currentPassageIndex >= totalPassages - 1} className="min-h-[44px]">
                <span className="hidden sm:inline mr-1">Next</span><ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>

          <p className="text-xs text-muted-foreground/50 text-center mt-3 sm:hidden select-none">Swipe left / right to navigate passages</p>
        </div>

        {/* Calendar View */}
        {showCalendar && <ReadingCalendar plan={plan} currentDay={currentDay} onDaySelect={handleNavigateDay} />}

        {/* Current Passage */}
        {currentPassage && (
          <div className="bg-card rounded-lg border border-border overflow-hidden">
            {/* Passage Header */}
            <div className="border-b border-border p-6">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                  <h2 className="text-xl font-heading font-semibold text-foreground mb-1">{currentPassage?.reference_text}</h2>
                  <p className="text-sm text-muted-foreground">{currentPassage?.verses?.length || 0} verses • Est. {calculateReadingTime(currentPassage?.verses)}</p>
                </div>
                <div className="flex items-center space-x-2">
                  <button onClick={handleOpenAudio} title="Listen to this chapter" className="flex items-center justify-center w-9 h-9 rounded-full bg-amber-500/10 hover:bg-amber-500/20 border border-amber-500/20 hover:border-amber-500/50 text-amber-500 transition-all duration-200 active:scale-95" aria-label="Open audio player">
                    <Headphones className="w-4 h-4" />
                  </button>
                  {!completedPassages?.has(currentPassageIndex) && (
                    <Button onClick={handleMarkComplete} size="sm" disabled={savingProgress} className="flex items-center space-x-2 min-h-[44px]">
                      {savingProgress ? <Icon name="Loader" size={16} className="animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                      <span>{savingProgress ? 'Saving...' : 'Mark Complete'}</span>
                    </Button>
                  )}
                  {completedPassages?.has(currentPassageIndex) && (
                    <div className="flex items-center space-x-2 text-success">
                      <CheckCircle className="w-5 h-5" /><span className="font-medium">Completed</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Bible Text */}
            <div className="p-6">
              {currentPassage?.verses?.length > 0 ? (
                <div className="space-y-0">
                  {currentPassage?.verses?.map((verse, index) => {
                    const verseKey = `${verse?.book_name}-${verse?.chapter}-${verse?.verse_number}`;
                    const isReflecting = reflectingVerseId === verseKey;
                    return (
                      <div key={verse?.verse_id || index} className="group hover:bg-muted/30 px-3 py-1 rounded-lg transition-colors duration-200">
                        <div className="flex items-start space-x-3">
                          <span className="text-sm font-semibold text-primary min-w-[2rem] pt-0.5">{verse?.verse_number}</span>
                          <div className="flex-1">
                            <p className="text-foreground leading-relaxed">{verse?.text}</p>
                            {/* Verse Actions */}
                            <div className="flex items-center space-x-2 mt-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                              <button onClick={() => handleAddVerseToLibrary(verse)} className="text-xs text-muted-foreground hover:text-primary flex items-center space-x-1 min-h-[36px] px-2">
                                <Plus className="w-3 h-3" /><span>Add to Library</span>
                              </button>
                              <span className="text-muted-foreground">•</span>
                              <button onClick={() => handleVerseReflect(verse)} className={`text-xs flex items-center space-x-1 min-h-[36px] px-2 transition-colors ${isReflecting ? 'text-primary' : 'text-muted-foreground hover:text-primary'}`}>
                                <Feather className="w-3 h-3" /><span>Reflect</span>
                              </button>
                            </div>
                            {/* Inline Reflection Form */}
                            {isReflecting && (
                              <InlineReflectionForm
                                verse={verse}
                                version={plan?.preferred_bible_version || 'kjv'}
                                onClose={() => setReflectingVerseId(null)}
                                onSaved={() => setReflectingVerseId(null)}
                              />
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Icon name="BookOpen" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-4" />
                  <p className="text-muted-foreground">No verses available for this passage</p>
                </div>
              )}
            </div>

            {/* Passage Footer */}
            <div className="border-t border-border p-4 bg-muted/30">
              <div className="flex items-center justify-between">
                <p className="text-sm text-muted-foreground">Passage {currentPassageIndex + 1} of {totalPassages}</p>
                {allCompleted && (
                  <div className="flex items-center space-x-2 text-success">
                    <CheckCircle className="w-4 h-4" /><span className="text-sm font-medium">All passages completed!</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Add to Library Modal — rendered via portal to avoid overflow/transform clipping */}
      {addToLibraryVerse && createPortal(
        <AddToLibraryModal
          isOpen={isAddToLibraryOpen}
          onClose={() => { setIsAddToLibraryOpen(false); setAddToLibraryVerse(null); }}
          book={addToLibraryVerse?.book}
          chapter={addToLibraryVerse?.chapter}
          version={addToLibraryVerse?.version}
          verses={addToLibraryVerse?.verses}
          selectedVerseNumber={addToLibraryVerse?.selectedVerseNumber}
        />,
        document.body
      )}
    </>
  );
};

export default ReadingInterface;